import { makeStyles, MenuItem, Select as MuiSelect } from "@material-ui/core";
import SelectArrowIcon from "assets/SelectArrowIcon";
import clsx from "clsx";
import { FieldConfig, useField } from "formik";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import React from "react";

type option = { title: string; value: any; children?: number[] };
interface Props extends FieldConfig {
  label?: string;
  multiple?: boolean;
  dir?: string;
  placeholder?: string;
  options: option[];
  disabled?: boolean;
  handleFieldChange?: (value: string, name: string) => void;
  values?: any;
  isLoadingData?: boolean;
  variant?: "standard" | "outlined" | "filled";
  className?: string;
}

export const useStyles = makeStyles({
  item: {
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "20px",
    justifyContent: (props: any) => (props.rtl ? "end" : "start"),
  },
  select: {
    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
    borderRadius: "6px",
    height: "38px",
    fontFamily: "Dubai",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "20px",
    backgroundColor: "transparent !important",
    "&.Mui-focused": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgb(6, 156, 240)",
      },
    },
    outline: "none !important",
    "&:active": {
      border: "none !important",
      outline: "none !important",
    },
    "&:focus": {
      backgroundColor: "transparent",
    },
    "&:hover": {
      backgroundColor: "transparent",
    },
    "&::before": {
      borderBottom: "none !important",
    },
    "&::after": {
      borderBottom: "none !important",
    },
  },
  selected: {
    "&:focus": {
      backgroundColor: "transparent",
    },
  },
  textArea: {
    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
    borderRadius: "6px",
  },
  notchedOutline: { borderColor: "#D1D5DB" },
  label: {
    fontFamily: "Dubai",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "20px",
    color: "#374151",
    marginTop: "1em",
  },
  disabled: {
    color: "#c9c9c9",
  },
  flexCol: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  icon: {
    top: "calc(50% - 5px)",
    width: "1em",
    height: "1em",
  },
  noLegend: {
    "& legend": {
      display: "none !important",
    },
  },
});

/**
 * Can only be used in Formik form
 * */
const FormikSelect = (props: Props) => {
  const { rtl } = useAppContext();
  const classes = useStyles({ rtl });
  const { variant, className, label, handleFieldChange, ...rest } = props;
  const [field, meta] = useField(rest);

  const extraProps = handleFieldChange
    ? {
        onChange: (e: any) => {
          handleFieldChange(e.target.value, props.name);
        },
      }
    : {};

  return (
    <div className={classes.flexCol}>
      <p
        className={clsx(classes.label, props.disabled ? classes.disabled : "")}
      >
        {props.label}
      </p>
      <MuiSelect
        MenuProps={{
          anchorOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          getContentAnchorEl: null,
        }}
        placeholder={props.placeholder}
        classes={{ icon: classes.icon, select: classes.selected }}
        IconComponent={SelectArrowIcon}
        className={clsx(classes.select, classes.noLegend)}
        fullWidth
        variant={props.options?.length < 1 ? "filled" : "outlined"}
        {...field}
        {...props}
        error={meta.touched && Boolean(meta.error)}
        disabled={props.disabled || props.isLoadingData}
        {...extraProps}
        displayEmpty
      >
        {props.options?.map((option, index) => {
          return (
            <MenuItem key={index} className={classes.item} value={option.value}>
              {option.title}
            </MenuItem>
          );
        })}
      </MuiSelect>
    </div>
  );
};

export default FormikSelect;
